import { useGetForm } from "@/workflow_api/services/nodes.api";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData } from "@/workflow_api/types/workflow";
import { Icon } from "@elaniintc/goes-design-system";
import { VariableKeyBox } from "../key-box";
import { cn } from "@/utils/theme";
import { useGetTableDetailsByTableName } from "@/queries/data-center/data-center.api";

interface Props {
  id: string;
}

interface FormData {
  steps: Array<{
    name: string;
    sections: Array<{
      fields: Array<{
        name: string;
        label: string;
        type: string;
        config?: Record<string, string | number | boolean | object>;
      }>;
    }>;
  }>;
}

const InputOutputBlock = ({
  form,
  isLast,
  index,
}: {
  form: FormData["steps"][number];
  isLast: boolean;
  index: number;
}) => {
  return (
    <div className="flex">
      <div
        className={cn(
          "w-0.5 relative translate-x-[25px]",
          isLast ? "bg-transparent" : "bg-neutral-object-accent",
        )}
      >
        <span className="absolute left-3 top-[14px] w-2 h-2 bg-neutral-object-accent rounded-full"></span>
        <span className="absolute w-3 h-5 border-l-2 border-t-2 border-neutral-object-accent rotate-x-180 rounded-tl-md"></span>
      </div>

      <div className="relative pl-[50px] mb-4 w-full">
        <div className="py-1">
          <h3 className="mb-4 space-x-2">
            <span className="text-body-quiet">Paso {index}</span>
            <span className="text-body-loud">{form.name}</span>
          </h3>
          <div className="space-y-3">
            {form.sections
              .flatMap((section) => section.fields)
              .map((field, index) => {
                let type = field.type;
                if (type === "switch") {
                  type = "boolean";
                }
                if (type === "file") {
                  if (field?.config?.multiple) {
                    type = "array";
                  } else {
                    type = "string";
                  }
                }
                if (type === "select") {
                  if (
                    !field?.config?.multiple &&
                    field?.config?.options_type === "database"
                  ) {
                    return (
                      <ExpandedSelectOutput
                        field_name={field.name}
                        data_center_table={
                          field.config?.data_center_table as string
                        }
                        source={field.config?.source as string}
                      />
                    );
                  }

                  if (field.config?.data_center_value_type === "boolean") {
                    type = "boolean";
                  } else if (
                    field.config?.data_center_value_type === "number"
                  ) {
                    type = "number";
                  } else {
                    type = "string";
                  }
                }
                return (
                  <VariableKeyBox
                    name={field.label || field.name}
                    type={
                      type as "string" | "number" | "boolean" | "array" | "date"
                    }
                    key={`field-${field.name}-${index}`}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExpandedSelectOutput = ({
  field_name,
  data_center_table,
  source,
}: {
  field_name: string;
  data_center_table: string;
  source: string;
}) => {
  const { data, isFetching } = useGetTableDetailsByTableName(
    data_center_table,
    source,
  );

  if (isFetching) {
    return (
      <Icon
        name="spinner-third"
        className="animate-spin text-sm text-on-disabled"
        type="far"
      />
    );
  }

  if (!data) {
    return null;
  }

  if (data?.data instanceof Array) {
    return data?.data?.map(({ id, name, type }) => {
      return (
        <VariableKeyBox
          name={`${field_name} - ${name}`}
          type={
            type.toLowerCase() as
              | "string"
              | "number"
              | "boolean"
              | "array"
              | "date"
          }
          key={`data-center-column-${id}`}
        />
      );
    });
  }

  return (
    data?.data as { columns: Array<{ id: string; name: string; type: string }> }
  ).columns.map(({ id, name, type }) => {
    return (
      <VariableKeyBox
        name={`${field_name} - ${name}`}
        type={
          type.toLowerCase() as
            | "string"
            | "number"
            | "boolean"
            | "array"
            | "date"
        }
        key={`data-center-column-${id}`}
      />
    );
  });
};

const Content = ({ id }: Props) => {
  const { data } = useGetForm(id);
  if (!data) {
    return (
      <Icon
        name="spinner-third"
        className="animate-spin text-sm text-on-disabled"
        type="far"
      />
    );
  }
  const formData = data.data as FormData;
  return formData.steps.map((step, index) => (
    <InputOutputBlock
      form={step}
      key={`step-${step.name}-${index}`}
      isLast={false}
      index={index + 1}
    />
  ));
};

export const FormVariables = ({ id }: Props) => {
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(id);
  if (!node) return <></>;
  const nodeData = node.data as unknown as NodeData;
  const form = nodeData?.inputs?.find((input) => input.name === "form")
    ?.value as string;
  return <Content id={form || ""} />;
};
